import React, {KeyboardEvent, useCallback, useMemo} from "react"
import DatePicker, {ReactDatePickerCustomHeaderProps} from "react-datepicker"
import {useIntl} from "react-intl"
import {DatePickerHeader} from "./datepicker-header"
import {Locale, LOCALE_FORMAT} from "../locale"
import "react-datepicker/dist/react-datepicker.css"
import {DateOrYear, InputMode} from "./date-or-year"


/**
 * Documentation at https://reactdatepicker.com/#example-default
 */
export function CustomDatePicker({ id, value, onChange, className }: {
    id: string,
    value?: DateOrYear,
    onChange: (value: DateOrYear | null) => void,
    className?: string
}) {
    const selectedDate = useMemo(() => value?.mode === InputMode.YEAR ? new Date(value.year, 0, 1) : value?.date, [value])
    const dateFormat = useMemo(() => value?.mode === InputMode.YEAR ? "yyyy" : "dd-MM-yyyy", [value])
    const calendarIconClass = useMemo(() => `datepicker-calendar-icon ${value ? "has-date" : ""}`, [value])
    const intl = useIntl()

    const renderHeader = useCallback((props: ReactDatePickerCustomHeaderProps) => (
        <DatePickerHeader date={props.date} decreaseMonth={props.decreaseMonth} increaseMonth={props.increaseMonth} changeYear={props.changeYear} intlLocale={intl.locale}/>
    ), [intl.locale])

    const popperModifiers = useMemo(() => [{
        name: "bottomOffset",
        fn(state: { placement: string, y: number }) {
            if (state.placement.startsWith("top")) state.y -= 8
            return state
        }
    }], [])

    const handleManualInput = useCallback((e: KeyboardEvent<HTMLElement>) => {
        const { selectionStart, selectionEnd, value } = e.currentTarget as HTMLInputElement
        if (["Backspace", "Delete"].includes(e.key)) {
            const beforeErase = value.slice(0, selectionStart ?? 0)
            const afterErase = value.slice(selectionEnd ?? value.length)
            const newValue = beforeErase + afterErase
            if (/^\d{4}$/.test(newValue)) onChange(new DateOrYear({ mode: InputMode.YEAR, year: Number(newValue) }))
            else if (newValue.length === 0) onChange(null)
            return
        }
        if (["ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) return
        if (!/^\d$/.test(e.key) || value.length >= 4) {
            e.preventDefault()
            return
        }
        if (value.length + 1 === 4) onChange(new DateOrYear({ mode: InputMode.YEAR, year: Number(value + e.key) }))
    }, [onChange])

    const customDatePicker = useMemo(() => (
        <DatePicker
            id={id}
            renderCustomHeader={renderHeader}
            showIcon
            isClearable
            calendarIconClassName={calendarIconClass}
            todayButton={intl.formatMessage({ id: "creator.calendar.today" })}
            portalId="root-portal"
            autoComplete="off"
            dateFormat={dateFormat}
            calendarStartDay={1}
            minDate={new Date(1000, 1, 1)}
            popperModifiers={popperModifiers}
            selected={selectedDate}
            onKeyDown={handleManualInput}
            onChange={(date) => {
                if (!date) onChange(null)
                else onChange(new DateOrYear({ mode: InputMode.DATE, date }))
            }}
        />
    ), [id, intl, renderHeader, calendarIconClass , dateFormat, popperModifiers, selectedDate, handleManualInput, onChange])

    return (
        <div id="datepicker-wrapper" tabIndex={0} className={className} onMouseDown={(e) => {
            // Stop mousedown only for clicks inside the popper
            if ((e.target as HTMLElement).closest(".react-datepicker")) e.stopPropagation()
        }}>
            {customDatePicker}
        </div>
    )
}

export function formatDateOrYear(dateOrYear?: DateOrYear): string | undefined {
    return dateOrYear ? dateOrYear.mode === InputMode.DATE
            ? dateOrYear.date.toLocaleDateString(LOCALE_FORMAT[Locale.EN], {day: "2-digit", month: "short", year: "numeric"})
            : dateOrYear.year.toString()
        : undefined
}